import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Pagination"
import { handleStopPropagation } from "components/Common/Utils"
import Filters from "components/Shared/Filters"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import Spinners from "components/Shared/Spinners"
import { usePermissions } from "hooks"
import { Trash } from "lucide-react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { Button, Card, CardBody, Table } from "reactstrap"
import {
  deleteInterviewAppointmentCandidate,
  getInterviewsAppointmentInvitedUser,
} from "store/actions/interviewsAction"
import { showAlert } from "store/reducers/alertReducer"
import {
  setInvitedUserPayload as setPayload,
  resetInvitedUserPayload as resetPayload,
} from "store/reducers/interviewsReducer.js"

const InterviewAppointmentInvitedUsers = () => {
  const {
    isLoading3,
    interviewAppointmentInvitedUsers,
    perPage,
    totalPages,
    totalCount,
    currentPage,
    isLoading,
    invitedUserPayload: payload,
  } = useSelector(state => state.Interviews)

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { showAll, permissions } = usePermissions("interview_schedule")
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [trigger, setTrigger] = useState(false)

  useEffect(() => {
    getAllData()
  }, [trigger])

  const getAllData = () => {
    dispatch(getInterviewsAppointmentInvitedUser(payload))
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const handleSearch = () => {
    dispatch(setPayload({ page_number: 1 }))
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const filters = {
    keyword: {
      type: "text",
      placeholder: t("Search by name,national id or phone number"),
      label: t("Search By"),
      colSize: 4,
    },
    type: {
      type: "select",
      placeholder: t("Select Status"),
      label: t("Select Status"),
      options: [
        { value: -1, label: t("All") },
        { value: 0, label: t("Invited") },
        { value: 1, label: t("Accepted") },
        { value: 2, label: t("Completed") },
        { value: 3, label: t("Cancelled") },
      ],
      colSize: 4,
    },
    order_by: {
      type: "select",
      placeholder: t("Select Order"),
      label: t("Select Order"),
      options: [
        { value: 1, label: t("Ascending") },
        { value: -1, label: t("Descending") },
      ],
      colSize: 4,
    },
  }

  const handleDelete = () => {
    dispatch(deleteInterviewAppointmentCandidate(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <>
      <div>
        {isLoading3 ? (
          <Spinners />
        ) : (
          <div>
            <Filters
              filters={filters}
              state={{ ...payload.filters, order_by: payload.order_by }}
              setState={({ keyword, type, order_by }) => {
                dispatch(
                  setPayload({
                    order_by: Number(order_by),
                    filters: {
                      ...payload.filters,
                      keyword,
                      type: Number(type),
                    },
                  })
                )
              }}
              handleSearch={handleSearch}
              handleReset={handleReset}
            />
            <Card className="user_personal_info mt-3 theme_card">
              <CardBody>
                <div className="table-responsive">
                  {!isLoading3 &&
                  interviewAppointmentInvitedUsers?.length === 0 ? (
                    <NoDataAvailable />
                  ) : (
                    <Table className="table mb-0">
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Interview Title")}</th>
                          <th>{t("Start Date")}</th>
                          <th>{t("End Date")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone")}</th>
                          <th>{t("National Id")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interviewAppointmentInvitedUsers?.map(
                          (item, index) => (
                            <tr key={index}>
                              <th>{(currentPage - 1) * perPage + index + 1}</th>
                              <td>{item?.interview?.title}</td>
                              <td>
                                <p className="ltr fit_content">
                                  {item?.interview?.start_date} ~{" "}
                                  {item?.interview?.start_time}
                                </p>
                              </td>
                              <td>
                                <p className="ltr fit_content">
                                  {item?.interview?.end_date}{" "}
                                  {item?.interview?.end_time}
                                </p>
                              </td>
                              <td>{item?.user?.name}</td>
                              <td>
                                <p className="ltr fit_content">
                                  {item?.user?.phone}
                                </p>
                              </td>
                              <td>{item?.user?.national_id}</td>
                              <td>
                                <p
                                  className={`${
                                    item?.status === 0
                                      ? "text-secondary"
                                      : item?.status === 1
                                      ? "text-primary"
                                      : item?.status === 2
                                      ? "text-success"
                                      : item?.status === 3
                                      ? "text-danger"
                                      : ""
                                  }`}
                                >
                                  {item?.status === 0
                                    ? t("Invited")
                                    : item?.status === 1
                                    ? t("Accepted")
                                    : item?.status === 2
                                    ? t("Completed")
                                    : item?.status === 3
                                    ? t("Cancelled")
                                    : ""}
                                </p>
                              </td>
                              <td onClick={handleStopPropagation}>
                                {!(
                                  item?.status === 2 || item?.status === 3
                                ) && (
                                  <div className="d-flex gap-2 action_buttons">
                                    {(showAll ||
                                      permissions.includes(
                                        "can_delete_invitation"
                                      )) && (
                                      <Button
                                        color="danger"
                                        onClick={() => {
                                          setItemIdToDelete(item.id)
                                          setShowDeleteModal(true)
                                        }}
                                        disabled={
                                          isLoading &&
                                          item?.uuid === itemIdToDelete
                                        }
                                        title={t("Remove")}
                                      >
                                        <Trash />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
            {!isLoading3 && interviewAppointmentInvitedUsers.length > 0 && (
              <Paginations
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={onPageChange}
              />
            )}
          </div>
        )}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Candidate"),
        })}
      />
    </>
  )
}

export default InterviewAppointmentInvitedUsers
